import type { NavLink, BoardSpec, RaceMode, ShopProduct, Model3D, RankEntry, CollisionRecord, LapRecord, CertLevel, CourseCard, EventItem } from '@/types';

// ---- Site-wide Navigation ----
export const NAV_LINKS: NavLink[] = [
  { label: '产品', href: '#boards' },
  { label: '赛事', href: '#arena' },
  { label: '商城', href: '#shop' },
  { label: '社区', href: '#models' },
  { label: '课程', href: '#education' },
  { label: '排行榜', href: '#rankings' },
];

// ---- Section 2: Four Motherboard Tiers ----
export const BOARDS: BoardSpec[] = [
  {
    id: 'C',
    name: 'C 板 / 入门之选',
    subtitle: '拼装即用，开启你的第一场对战',
    color: 'board-c',
    bgColor: 'rgba(0, 230, 118, 0.08)',
    image: '/images/board_a.webp',
    specs: [
      { label: '全功能口', value: '2 (I2C / UART / GPIO / INT)' },
      { label: 'GPIO 口', value: '2 (开关量)' },
      { label: 'MCU', value: 'GD32E230C8T6' },
      { label: '板载', value: 'IMU + 2.4G + 双电机驱动' },
    ],
    price: 'XX',
    ageGroup: '少年组 6-9 岁',
    programmingLang: '图形化编程',
  },
  {
    id: 'B',
    name: 'B 板 / 进阶之梯',
    subtitle: 'Python 编程，传感器全开',
    color: 'board-b',
    bgColor: 'rgba(0, 194, 255, 0.08)',
    image: '/images/board_b.webp',
    specs: [
      { label: '全功能口', value: '4 (I2C / UART / GPIO / INT)' },
      { label: 'GPIO 口', value: '2 (开关量)' },
      { label: 'MCU', value: 'GD32F405RGT6' },
      { label: '板载', value: 'IMU + Wi-Fi/BLE + 摄像头接口' },
    ],
    price: 'XX',
    ageGroup: '青少组 10-13 岁',
    programmingLang: 'Python',
  },
  {
    id: 'A',
    name: 'A 板 / 竞赛之刃',
    subtitle: 'NPU 加速，视觉 + AI 策略',
    color: 'board-a',
    bgColor: 'rgba(255, 184, 0, 0.08)',
    image: '/images/board_c.webp',
    specs: [
      { label: '全功能口', value: '8 (I2C / UART / GPIO / INT)' },
      { label: 'GPIO 口', value: '4 (开关量)' },
      { label: 'MCU', value: 'STM32N6570 / K230' },
      { label: '板载', value: 'IMU + Wi-Fi/BLE + MIPI 摄像头 + 8MB PSRAM' },
    ],
    price: 'XX',
    ageGroup: '菁英组 14-16+ 岁',
    programmingLang: 'Python / C++',
  },
  {
    id: 'S',
    name: 'S 板 / 极客之巅',
    subtitle: '双芯片架构，Linux + ROS，自建整车',
    color: 'board-s',
    bgColor: 'rgba(255, 59, 59, 0.08)',
    image: '/images/board_s.webp',
    specs: [
      { label: '全功能口', value: '12 (I2C / UART / GPIO / INT)' },
      { label: 'GPIO 口', value: '4 (开关量)' },
      { label: '架构', value: 'RK3566 (Linux/ROS) + GD32E230 (RTOS)' },
      { label: '板载', value: 'IMU + Wi-Fi/BLE + 双目摄像头 + 2GB RAM + 32GB eMMC' },
    ],
    price: 'XX',
    ageGroup: '极客组 不限年龄',
    programmingLang: 'C++ / Rust / RTOS',
  },
];

// ---- Competition: Three Tiers ----
export const COMPETITION_TIERS = [
  {
    id: 'plan1',
    name: '自由对战',
    tagline: '随时随地，画个圈就能打',
    scene: '课后 / 家中 / 公园 / 走廊',
    venue: '地面画圈即可',
    referee: '无，选手自觉',
    system: '无',
    description: '两个同学在课间、放学后、周末，地面画个圈就能开战。翻车或出界即输，胜负凭自觉，乐趣在切磋。',
  },
  {
    id: 'plan2',
    name: '校内联赛',
    tagline: '班级赛、校级选拔',
    scene: '教室 / 操场 / 社团活动室',
    venue: '模块化拼接场地（简易版）',
    referee: '教师 / 学生志愿者',
    system: '基础裁判工具包（秒表/电子秤/通规/记分板）',
    description: '学校组织班级赛和年级赛，优胜者获得官方认证赛参赛资格。含赛前检录（称重+通规+武器检查），三局两胜淘汰制。',
  },
  {
    id: 'plan3',
    name: '官方认证赛',
    tagline: '城市赛、全国赛、总决赛',
    scene: '品牌场馆 / 大型赛事',
    venue: '全规格场地 + LED 围栏 + 大屏',
    referee: '认证裁判团队',
    system: '完整裁判系统（IMU 碰撞检测 + 实时大屏 + KO 回放）',
    description: '最高级别赛事。完整裁判系统（IMU 碰撞检测 + 实时大屏 + KO 回放）+ 赛季积分体系。爆发机制打破僵局——每车每局 3 次 8 秒 100% 动力爆发。团队赛 2v2 同步开放。',
  },
];

// ---- Competition: Three Competition Types ----
export const COMPETITION_TYPES = [
  {
    id: 'combat-1v1',
    category: '对抗赛',
    title: '个人对抗赛 1v1',
    description: '一对一正面交锋。3 分钟一局，三局两胜。翻车、出界、无法移动超过 10 秒即判负。每车每局限 3 次爆发，每次 8 秒 PWM 100% 输出——常态 85% 固件锁死，选手编程决定触发时机。',
    image: '/images/race_mode_pvp_1v1.webp',
  },
  {
    id: 'combat-2v2',
    category: '对抗赛',
    title: '团队对抗赛 2v2',
    description: '两队各 2 人，对角站位。队友碰撞不判罚。一车出局后留在场内做静止障碍物。每车每局限 3 次爆发，每次 8 秒 100% 动力。可报 2-3 人（2 正选 + 1 替补），局间可换人也可换车。',
    image: '/images/race_mode_pvp_2v2.webp',
  },
  {
    id: 'racing',
    category: '竞速赛',
    title: '竞速计时赛',
    description: '赛道宽 400-600mm，单圈 15-25m（方案 2 为 10-20m），含平路/坡道/颠簸/碎石/窄桥五种地形。排位赛单车计时 3 圈取最快圈速，正赛 2-4 车同发按到达顺序排名。允许合理碰撞推挤，翻车或出界即 DNF。方案 2 用秒表人工计时，方案 3 裁判系统自动计时。',
    image: '/images/race_mode_racing.webp',
  },
  {
    id: 'battle-royale',
    category: '乱斗赛',
    title: '四人乱斗 Battle Royale',
    description: '4 车同场混战，无队友、无结盟。四角站位车头朝中心，5 分钟上限。最后存活者直接获胜；时间到按 KO 数排名。每车每局限 3 次爆发，每次 8 秒。出局车辆立即移出场外。预赛每组前 2 晋级，决赛单局定冠亚季殿。',
    image: '/images/race_mode_battle_royale.webp',
  },
];

// ---- Competition: Four Groups ----
export const COMPETITION_GROUPS = [
  { name: '少年组', age: '6-9 岁', board: 'C 板', weight: '800g', weapon: '仅碰撞推挤', power: '标准动力，转速锁定', scheme: '仅方案 1' },
  { name: '青少组', age: '10-13 岁', board: 'B 板', weight: '1.2kg', weapon: '推铲类无动力武器', power: '可换电机/齿比' },
  { name: '菁英组', age: '14-16+ 岁', board: 'B / A 板', weight: '1.8kg', weapon: '全部合规武器', power: '全部开放' },
  { name: '极客组', age: '不限', board: 'A / S 板', weight: '2.5kg', weapon: '全部合规武器（含自研）', power: '无刷电机 + 高电压电池' },
];

// ---- Section 5: Shop Products ----
export const SHOP_CATEGORIES = ['全部', '轮胎', '装甲', '传感器', '电机', '主板', '3D 打印件', '工具包'];

export const SHOP_PRODUCTS: ShopProduct[] = [
  {
    id: 'tyre-road',
    name: '公路竞速轮胎',
    image: '/images/shop_tyre_road.webp',
    category: '轮胎',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 1523,
    rating: 4.8,
  },
  {
    id: 'tyre-offroad',
    name: '越野全地形轮胎',
    image: '/images/shop_tyre_offroad.webp',
    category: '轮胎',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 982,
    rating: 4.7,
  },
  {
    id: 'armor-front',
    name: '楔形前铲装甲',
    image: '/images/shop_armor_front.webp',
    category: '装甲',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 2105,
    rating: 4.9,
  },
  {
    id: 'armor-side',
    name: '侧翼防护装甲（一对）',
    image: '/images/shop_armor_side.webp',
    category: '装甲',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 876,
    rating: 4.6,
  },
  {
    id: 'sensor-ir',
    name: '红外避障传感器',
    image: '/images/shop_sensor_ir.webp',
    category: '传感器',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 3102,
    rating: 4.8,
  },
  {
    id: 'sensor-color',
    name: '颜色识别传感器',
    image: '/images/shop_sensor_color.webp',
    category: '传感器',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 1845,
    rating: 4.7,
  },
  {
    id: 'sensor-ultrasonic',
    name: '超声波测距模块',
    image: '/images/shop_ultrasonic.webp',
    category: '传感器',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 2340,
    rating: 4.8,
  },
  {
    id: 'motor-torque',
    name: '高扭矩 130 电机',
    image: '/images/shop_motor_high_torque.webp',
    category: '电机',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    sales: 4102,
    rating: 4.9,
  },
  {
    id: 'battery-18650',
    name: '18650 锂电池',
    image: '/images/shop_battery_18650.webp',
    category: '工具包',
    creator: '官方',
    creatorType: 'official',
    price: 'XX',
    originalPrice: 'XX',
    sales: 5600,
    rating: 4.9,
  },
];

// ---- Section 6: 3D Model Library ----
export const MODEL_CATEGORIES = ['最新', '最热', '推铲', '装甲', '支架', '外观件', '工具件'];

export const MODELS_3D: Model3D[] = [
  {
    id: 'plow-v2',
    name: '竞赛级楔形推铲 V2',
    image: '/images/model_plow.webp',
    creator: 'Maker_X',
    downloads: 2340,
    commission: 25,
    category: '推铲',
  },
  {
    id: 'side-armor',
    name: '轻量化侧翼装甲',
    image: '/images/model_side_armor.webp',
    creator: 'RacingDragon',
    downloads: 1876,
    commission: 15,
    category: '装甲',
  },
  {
    id: 'camera-mount',
    name: '可调角度摄像头支架',
    image: '/images/model_camera_mount.webp',
    creator: 'TechKnight',
    downloads: 956,
    commission: 15,
    category: '支架',
  },
];

// ---- Section 7: Data Center ----
export const SEASON_RANKINGS: RankEntry[] = [
  { rank: 1, name: '铁犀牛', team: '雷霆战队', score: 1350, winRate: 92 },
  { rank: 2, name: '闪电侠', team: '疾风战队', score: 1280, winRate: 88 },
  { rank: 3, name: '磐石', team: '坚盾战队', score: 1220, winRate: 85 },
  { rank: 4, name: '暗夜猎手', team: '夜鹰战队', score: 1160, winRate: 82 },
  { rank: 5, name: '烈焰风暴', team: '赤焰战队', score: 1120, winRate: 80 },
  { rank: 6, name: '银月', team: '月光战队', score: 1080, winRate: 78 },
  { rank: 7, name: '暴风', team: '旋风战队', score: 1040, winRate: 76 },
  { rank: 8, name: '冰霜', team: '极寒战队', score: 1010, winRate: 74 },
];

export const TOP_COLLISIONS: CollisionRecord[] = [
  { rank: 1, redPlayer: '铁犀牛', bluePlayer: '闪电侠', gForce: 3.8, date: '2026-07-15' },
  { rank: 2, redPlayer: '烈焰风暴', bluePlayer: '磐石', gForce: 3.5, date: '2026-07-08' },
  { rank: 3, redPlayer: '暗夜猎手', bluePlayer: '暴风', gForce: 3.2, date: '2026-06-30' },
  { rank: 4, redPlayer: '银月', bluePlayer: '冰霜', gForce: 2.9, date: '2026-06-22' },
  { rank: 5, redPlayer: '铁犀牛', bluePlayer: '暴风', gForce: 2.8, date: '2026-06-15' },
];

export const TOP_LAPS: LapRecord[] = [
  { track: '竞速赛道 A', player: '闪电侠', time: '12.34', config: '公路胎 + 高转速电机' },
  { track: '技术赛道 B', player: '暗夜猎手', time: '18.56', config: '越野胎 + 标准电机' },
  { track: '综合赛道 C', player: '银月', time: '22.18', config: '全地形胎 + B板' },
  { track: '竞速赛道 A', player: '烈焰风暴', time: '12.89', config: '公路胎 + 四驱' },
  { track: '技术赛道 B', player: '磐石', time: '19.02', config: '越野胎 + 高扭矩电机' },
];

// ---- Section 8: Skill Certification ----
export const CERT_LEVELS: CertLevel[] = [
  { level: 1, title: '初级工程师', description: '独立完成整车拼装与基础操控' },
  { level: 2, title: '操控能手', description: '通过操控技能考核，掌握对抗基础' },
  { level: 3, title: '改装技师', description: '独立完成轮胎、装甲、齿比选配改装' },
  { level: 4, title: '编程新星', description: '使用 图形化编程 完成传感器编程任务' },
  { level: 5, title: 'Python 工程师', description: '使用 Python 编写自动对抗策略' },
  { level: 6, title: '竞赛高手', description: '赛季积分排名前 30% 或获得区域赛奖项' },
  { level: 7, title: '3D 设计师', description: '独立设计建模并 3D 打印结构件' },
  { level: 8, title: '创新大师', description: '自研车辆结构或 AI 策略，社区采纳获佣金' },
];

// ---- Section 9: Education Courses ----
export const COURSES: CourseCard[] = [
  {
    id: 'junior',
    title: '通识',
    ageRange: '入门 · 零基础可学',
    lessons: 16,
    skills: ['拼装', '操控', '物理实验', '对战入门'],
    subjects: ['物理', '数学', '通用技术', '英语', '人工智能'],
    boardLink: 'C 板',
    color: 'board-c',
    image: '/images/edu_junior.webp',
  },
  {
    id: 'teen',
    title: '进阶',
    ageRange: '掌握拼装后 · 开始编程',
    lessons: 16,
    skills: ['改装', '图形化编程 编程', '传感器实验', '对抗赛'],
    subjects: ['物理', '数学', '信息技术', '工程思维', '人工智能'],
    boardLink: 'B 板',
    color: 'board-b',
    image: '/images/edu_teen.webp',
  },
  {
    id: 'elite',
    title: '高阶',
    ageRange: '完成进阶后 · Python 与 AI',
    lessons: 16,
    skills: ['Python', '3D 打印', 'AI 策略', '工程设计循环'],
    subjects: ['物理', '数学', '信息技术', '通用技术', '人工智能'],
    boardLink: 'B / A 板',
    color: 'board-a',
    image: '/images/edu_elite.webp',
  },
  {
    id: 'master',
    title: '菁英',
    ageRange: '三方向模块 · 选 1-2 个方向',
    lessons: 32,
    skills: ['计算机视觉', '行为树AI', '传感器融合', '3D建模'],
    subjects: ['物理', '数学', '信息技术', '人工智能', '通用技术'],
    boardLink: 'A / S 板',
    color: 'board-s',
    image: '/images/edu_master.webp',
  },
  {
    id: 'geek',
    title: '极客',
    ageRange: '大学社团 / 成人爱好者',
    lessons: 0,
    skills: ['SLAM', '强化学习', '集群协作', '自研硬件'],
    subjects: ['人工智能', '信息技术', '数学', '通用技术', '英语'],
    boardLink: 'S 板',
    color: 'board-s',
    image: '/images/edu_geek.webp',
  },
];

// ---- Section 10: Events ----
export const UPCOMING_EVENTS: EventItem[] = [
  {
    date: '2026-09-15',
    name: '2026 秋季赛季开幕赛',
    location: '上海 · 官方赛场',
    status: 'open',
  },
  {
    date: '2026-10-20',
    name: '华南区校园联赛决赛',
    location: '深圳 · 南山文体中心',
    status: 'upcoming',
  },
  {
    date: '2026-11-05',
    name: '全国青少年科技创新赛',
    location: '北京 · 国家会议中心',
    status: 'upcoming',
  },
];

// ---- Footer Links ----
export const FOOTER_COLUMNS = [
  {
    title: '产品',
    links: ['战车介绍', '主板对比', '规格参数', '购买渠道'],
  },
  {
    title: '赛事',
    links: ['赛事列表', '规则手册', '报名参赛', '数据看板'],
  },
  {
    title: '商城',
    links: ['配件商城', '3D 模型库', '上传模型', '佣金计划'],
  },
  {
    title: '社区',
    links: ['资源中心', '教程 Wiki', '创作者中心', '会员体系'],
  },
  {
    title: '支持',
    links: ['课程资源', '教师培训', '认证考试', '关于我们'],
  },
];
